import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase'; // Adjust this import based on your firebase config location

const PowerbankCheckout = () => {
  const { powerbankNumber } = useParams();
  const [fullName, setFullName] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Validate powerbank number
  React.useEffect(() => {
    const pbNum = parseInt(powerbankNumber);
    if (isNaN(pbNum) || pbNum < 1 || pbNum > 10) {
      setMessage('Numéro de powerbank invalide. Doit être entre 1 et 10.');
      setMessageType('error');
    }
  }, [powerbankNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Validate powerbank number
    const pbNum = parseInt(powerbankNumber);
    if (isNaN(pbNum) || pbNum < 1 || pbNum > 10) {
      setMessage('Numéro de powerbank invalide.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    // Validate table number
    if (!tableNumber || tableNumber.trim() === '') {
      setMessage('Veuillez entrer un numéro de table.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      // Create new document in powerbank_checkouts collection
      const powerbankCheckoutsRef = collection(db, 'powerbank_checkouts');
      
      await addDoc(powerbankCheckoutsRef, {
        powerbankNumber: powerbankNumber,
        fullName: fullName.trim(),
        tableNumber: parseInt(tableNumber),
        checkoutTime: serverTimestamp(),
        timestamp: new Date().toISOString()
      });

      setMessage(`✓ Powerbank ${powerbankNumber} retiré avec succès!`);
      setMessageType('success');
      
      // Reset form after 2.5 seconds
      setTimeout(() => {
        setFullName('');
        setTableNumber('');
        setMessage('');
      }, 2500);

    } catch (error) {
      console.error('Error:', error);
      setMessage('Une erreur s\'est produite. Veuillez réessayer.');
      setMessageType('error');
    }

    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.powerbankIcon}>🔋</div>
          <h1 style={styles.title}>Powerbank #{powerbankNumber}</h1>
          <p style={styles.subtitle}>Retrait de powerbank</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Nom complet</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Entrez votre nom complet"
              required
              style={styles.input}
              disabled={loading}
              autoComplete="off"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Numéro de table</label>
            <input
              type="number"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="Ex: 1, 2, 3..."
              required
              min="1"
              style={styles.input}
              disabled={loading}
              autoComplete="off"
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
          >
            {loading ? (
              <span style={styles.buttonContent}>
                <span style={styles.spinner}></span>
                Enregistrement...
              </span>
            ) : (
              'Confirmer le retrait'
            )}
          </button>
        </form>

        {message && (
          <div
            style={{
              ...styles.message,
              ...(messageType === 'success' ? styles.messageSuccess : styles.messageError)
            }}
          >
            {message}
          </div>
        )}

        <div style={styles.footer}>
          <p style={styles.footerText}>WeStudy • Powerbank Service</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px 24px',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    position: 'relative'
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  powerbankIcon: {
    fontSize: '64px',
    marginBottom: '12px',
    animation: 'pulse 2s ease-in-out infinite'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '4px',
    margin: '0'
  },
  subtitle: {
    color: '#718096',
    fontSize: '14px',
    margin: '8px 0 0 0',
    fontWeight: '500'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#2d3748'
  },
  input: {
    padding: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '16px',
    transition: 'all 0.2s',
    outline: 'none',
    WebkitAppearance: 'none',
    appearance: 'none'
  },
  button: {
    padding: '16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '17px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    WebkitTapHighlightColor: 'transparent'
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none'
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    display: 'inline-block'
  },
  message: {
    marginTop: '20px',
    padding: '16px',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    textAlign: 'center',
    animation: 'slideIn 0.3s ease-out'
  },
  messageSuccess: {
    background: '#c6f6d5',
    color: '#22543d',
    border: '2px solid #9ae6b4'
  },
  messageError: {
    background: '#fed7d7',
    color: '#742a2a',
    border: '2px solid #fc8181'
  },
  footer: {
    marginTop: '32px',
    paddingTop: '20px',
    borderTop: '1px solid #e2e8f0',
    textAlign: 'center'
  },
  footerText: {
    fontSize: '13px',
    color: '#a0aec0',
    margin: '0'
  }
};

// Add CSS animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  input:focus {
    border-color: #667eea !important;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
  }
  
  button:active:not(:disabled) {
    transform: scale(0.98) !important;
  }
  
  /* Remove spinner arrows from number input */
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  input[type=number] {
    -moz-appearance: textfield;
  }
  
  @media (max-width: 480px) {
    body {
      font-size: 16px;
    }
  }
`;
document.head.appendChild(styleSheet);

export default PowerbankCheckout;
